/* eslint-disable react/no-danger */
/* eslint-disable import/no-extraneous-dependencies */
import { RadioGroup, Tooltip } from 'crux';
import React, { useEffect, useRef } from 'react';
import at from 'lodash/at';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTheme } from 'styled-components';
import get from 'lodash/get';
import {
  parseHtml,
  scrollQuestionIntoView,
  getUTCTimestamp,
  isQuotePage,
  sanitizeInnerHtml,
} from '../utils';

// import isPlainObject from 'lodash/isPlainObject';
import TextElement from '../Components/UIElements/TextElement';
import { questionType } from '../types';

import {
  getShape,
  getWidth,
  getLabelProps,
  getCSSVariables,
  getTooltipProps,
  getTimeStampData,
  getGroupQAriaLabel,
} from './utils';
import { QuestionDescription } from './utils/styles';
import { updateAnswersArray } from '../redux/actions';
import SelectWrapper, { TooltipWrapper } from './SelectWrapper';
import { submitSectionQuestion, updateQuestionAnswerTimeStamp } from '../redux/actions/questions';

const SingleSelect = ({
  question,
  optionalText,
  theme,
  sectionIndex,
  handleChange,
  handleActionBtn,
  isEditMode,
  screenViewport,
  lastFocusedElementId,
  role,
  updateTimeStamp,
  meta,
  questionsArray,
  // updateAnswerArrayInSocket,
}) => {
  const {
    display_type: displayType,
    question_id: questionId,
    response_options: options,
    question_text: questionText,
    question_description: questionDescription,
    is_readonly: isReadonly,
    hint_title: tooltipTitle,
    hint_text: tooltipBody,
    hint_html: tooltipHTML,
    response,
    orientation,
    tooltip,
    is_editable: isEditable,
    transaction_log: transactionLog,
    last_submitted_at: lastSubmittedAt,
    show_as_required: showAsRequired,
  } = question;
  const {
    pageId,
    config: {
      theme: {
        pageOverrides,
        components,
        global: { colorScheme, colorOverride, radioTheme, classicRadioTheme },
      },
    },
  } = theme;
  const showOptionalText = get(theme, 'config.theme.global.showOptionalText', false);
  const screenReaderLabelQuestionId = get(question, 'screen_reader_label_id', null);
  const hideCardButtonArrow = get(question, 'properties.hideCardArrow', false);
  const ariaLabel = getGroupQAriaLabel(screenReaderLabelQuestionId, questionsArray, pageId);

  // Tab Navigation
  useEffect(() => {
    const shouldFocus = lastFocusedElementId === questionId;
    if (shouldFocus) {
      const _options = document.getElementsByName(lastFocusedElementId);
      _options.forEach((element) => {
        if (element.checked) {
          element.focus();
        }
      });
    }
  }, []);

  // const checked = isPlainObject(response) ? response.id : response;

  const singleSelect = get(pageOverrides, [pageId, 'singleSelect'], components.singleSelect);
  const { shape, overlap, labelWidth } = getShape({
    questionId,
    displayType,
    singleSelect,
    question,
  });

  useEffect(() => {
    if (isEditMode || question.response) {
      const defaultOption = options.find((option) => option.id === response?.id) || {};
      updateTimeStamp({ transactionLog, lastSubmittedAt }, true);
      handleChange(defaultOption, true);
    }
  }, []);

  const labelProps = getLabelProps('singleSelect', components, pageId, pageOverrides);

  const cardButtonTheme = get(labelProps, 'cardButtonTheme', null);
  if (cardButtonTheme) {
    labelProps.cardButtonTheme.borderOnFocus =
      shape === 'card_button' && screenViewport === 'mobile'
        ? cardButtonTheme.borderOnFocusMobile
        : cardButtonTheme.borderOnFocusDesktop;
  }

  const themeConfiguration = {
    ...(colorOverride || radioTheme),
    ...(shape === 'classic' && classicRadioTheme),
    helperTextColor: get(question, 'style.helper_text_color'),
  };

  const timeStampRef = useRef(null);

  const hideTooltipWrapper = get(theme, 'config.theme.global.radio.hideTooltipWrapper', false);
  const { questionDescriptionColor } = get(theme, 'config.theme.global.text');

  // CSS Variables
  const element = '.singleSelect-description';
  const radioElement = `.singleSelect-${shape}`;
  const radioDesElement = '.singleSelect-label';
  const cssVariables = getCSSVariables(theme);
  const font = at(cssVariables, [`pageOverride.font${element}`, `global.font${element}`]);
  const radioDesFont = at(cssVariables, [
    `pageOverride.font${radioDesElement}`,
    `global.font${radioDesElement}`,
  ]);

  const radioFont = at(cssVariables, [
    `pageOverride.font${radioElement}`,
    `global.font${radioElement}`,
  ]);
  const labelTheme = get(labelProps, 'labelText.themeConfiguration', {});
  const themeConfig = {
    ...themeConfiguration,
    font: radioFont[0] || radioFont[1],
  };

  const disabled = displayType === 'product_card' ? isReadonly : isReadonly || !isEditable;
  // const isRequiredValid = get(question, 'validations.required.value', false);
  const isAsteriskConfig = get(theme, 'config.theme.components.inputAsteriskRequired');
  let isAsterisk;
  if (
    isAsteriskConfig &&
    question.validations.required &&
    question.validations.required.value === true
  ) {
    isAsterisk = true;
  }
  const tooltipProps = getTooltipProps(tooltipTitle, tooltipBody, tooltipHTML || tooltip);
  const handleFocus = (e) => {
    const el = e.target;
    scrollQuestionIntoView({
      element: el,
      theme,
      role,
      screenViewport,
    });
    // if(timeStampRef?.current?.timeStart)
    timeStampRef.current.timeStart = getUTCTimestamp();
    updateTimeStamp(getTimeStampData(timeStampRef, transactionLog, meta));
    // updateAnswerArrayInSocket();
  };

  const handleOnBlur = () => {
    updateTimeStamp(getTimeStampData(timeStampRef, transactionLog, meta));
  };

  const tooltipColor = get(theme, 'config.theme.global.toolTip.color', '');
  const tooltipFont = get(theme, 'config.theme.global.toolTip.font', '');

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <fieldset style={ { width: '100%' } }>
      <legend style={ { fontSize: 0, margin: 0 } }>
        {`${ariaLabel}  ${parseHtml(questionText)}`}
      </legend>
      <SelectWrapper
        ref={ timeStampRef }
        width={ getWidth('singleSelect', pageId, pageOverrides) }
        shape={ shape }
      >
        <TooltipWrapper
          id={ `radio-${shape}-label-${questionId}` }
          hideTooltipWrapper={ hideTooltipWrapper }
        >
          <TextElement
            styleType='label'
            text={ `${questionText} ${optionalText}`.trim() }
            questionObj={ question }
            sectionIndex={ sectionIndex }
            elementTheme={ labelTheme }
            baseFont={ radioDesFont[0] || radioDesFont[1] }
            id={ `${questionId}-questionText` }
            isAsterisk={ isAsterisk }
            ariaHidden
            showOptionalText={ showOptionalText && !isQuotePage() }
            isWcagOptional={ !(showAsRequired || question?.validations?.required?.value) }
          />
          {tooltipProps.hasTooltip && (
            <Tooltip
              ariaLabel={ `${questionText} info dialog` }
              removeAriaLabelforPopper
              color={ tooltipColor }
              themeConfiguration={ { tooltipFont, tooltipTextColor: tooltipColor } }
              { ...tooltipProps }
            />
          )}
        </TooltipWrapper>
        {!questionText && <span id={ `${questionId}-questionText` } />}
        {questionDescription && (
          <QuestionDescription color={ questionDescriptionColor } font={ font }>
            <span
              dangerouslySetInnerHTML={ {
                // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
                __html: sanitizeInnerHtml(questionDescription),
              } }
            />
          </QuestionDescription>
        )}

        {!questionText && <span id={ `${questionId}-questionText` } />}
        <div onFocus={ handleFocus }>
          <RadioGroup
            onBlur={ () => handleOnBlur() }
            onChange={ (e, option) => {
              handleChange(option, false, true);
            } }
            onTooltipFocus={ handleFocus }
            onActionBtnClick={ handleActionBtn }
            options={ options }
            name={ questionId }
            id={ questionId }
            overlap={ overlap }
            checked={ response ? response.id : '' }
            color={ colorScheme }
            radioFontColor={ radioTheme.fontColor }
            labelWidth={ labelWidth }
            themeConfiguration={ themeConfig }
            { ...labelProps }
            shape={ shape }
            disabled={ disabled }
            orientation={ orientation || 'rows' }
            isInputAriaLabel
            hideLabelTextSpan
            hideCardButtonArrow={ hideCardButtonArrow }
          />
        </div>
      </SelectWrapper>
    </fieldset>
  );
};

SingleSelect.propTypes = {
  question: questionType.isRequired,
  sectionIndex: PropTypes.number.isRequired,
};

const mapStateToProps = (state, props) => {
  const {
    questions,
    meta: { screenViewport },
  } = state;
  const { activeSubsectionId, sections, lastFocusedElementId, role } = questions;
  const { sectionIndex } = props;
  const currentStatus = get(props, 'theme.pageId', '');
  const questionsArray = get(sections[activeSubsectionId], `[${sectionIndex}].questionList`, []);
  const questionsObj = get(sections[activeSubsectionId], `[${sectionIndex}].questionObj`, []);

  let currentSectionAnswerList = [];
  if (sections) {
    let currentSection = [];
    if (activeSubsectionId && sectionIndex) {
      currentSection = sections[activeSubsectionId][sectionIndex];
    }
    if (currentSection && currentSection.answersArray) {
      currentSectionAnswerList = currentSection.answersArray;
    }
  }
  return {
    lastFocusedElementId,
    currentSectionAnswerList,
    screenViewport,
    role,
    meta: state.meta,
    questionsArray: currentStatus === 'plan' ? questionsObj : questionsArray,
  };
};

const mapDispatchToProps = (dispatch, props) => {
  const transactionLog = get(props, 'question.transaction_log', []);
  const questionId = get(props, 'question.question_id', '');
  return {
    handleChange: (response, preventReflexiveCall = false, hasEdit = false) => {
      const auditRequired = get(props, 'question.audit_required', false);
      const displayType = get(props, 'question.display_type', '');
      const currentStatus = get(props, 'theme.pageId', '');
      dispatch(
        updateAnswersArray({
          questionId,
          hasEdit,
          response: response.id,
          questionResponse: response,
          sectionIndex: props.sectionIndex,
          propQuestion: props.question,
          preventReflexiveCall,
          listParentId: props.listParentId,
          listIndex: props.listIndex,
          auditRequired,
          displayType,
          currentStatus,
          isNestedQ: props.isNestedQ,
          parentListIndex: props.parentListIndex,
          transactionLog,
        }),
      );
    },
    handleActionBtn: () => {
      dispatch(submitSectionQuestion(props.sectionIndex, {}));
    },
    updateTimeStamp: (timeStampData, isPrefilled = false) => {
      dispatch(
        updateQuestionAnswerTimeStamp(questionId, timeStampData, props.sectionIndex, isPrefilled),
      );
    },
    // updateAnswerArrayInSocket: () => {
    //   const childQuestions = get(props, 'question.child_questions', false);
    //   const reflexiveQuestionFlag = get(props, 'question.reflexive_question_flag', false);
    //   dispatch(updateQuestionAnswerInSocket(questionId, childQuestions || reflexiveQuestionFlag));
    // },
  };
};
export default withTheme(connect(mapStateToProps, mapDispatchToProps)(SingleSelect));
